import React, { useEffect } from "react";
import { MdChevronRight, MdChevronLeft } from "react-icons/md";

const Pagination = ({
  setPage,
  page,
  totalpage,
  currentPage,
  pageSize,
  setPageSize,
  arraySize,
}: {
  setPage: any;
  page?: number;
  totalpage?: number;
  currentPage?: number;
  pageSize?: number;
  setPageSize?: any;
  arraySize?: any;
}) => {
  const createPages = (count: number, current: number) => {
    const delta = 2;
    const pages: (number | "ellipsis")[] = [];
    for (let i = 1; i <= count; i++) {
      if (i === 1 || i === count || (i >= current - delta && i <= current + delta)) {
        pages.push(i);
      } else if (pages[pages.length - 1] !== "ellipsis") {
        pages.push("ellipsis");
      }
    }
    return pages;
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage, pageSize]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > totalpage) return;
    setPage(newPage);
  };

  const pages = createPages(totalpage ?? 1, currentPage ?? 1);
  const isFirst = currentPage <= 1;
  const isLast = currentPage >= totalpage;

  return (
    <div className="pgn-root">
      {/* Left: page info */}
      <div className="pgn-info">
        <span>Page</span>
        <span className="pgn-info-current">{currentPage}</span>
        <span>of</span>
        <span className="pgn-info-total">{totalpage}</span>
      </div>

      {/* Centre: page buttons */}
      <div className="pgn-pages">
        {/* Prev */}
        <button
          className={`pgn-nav-btn${isFirst ? " pgn-nav-btn--disabled" : ""}`}
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={isFirst}
          aria-label="Previous page"
        >
          <MdChevronLeft size={18} />
        </button>

        {pages.map((p, i) =>
          p === "ellipsis" ? (
            <span key={i} className="pgn-ellipsis">…</span>
          ) : (
            <button
              key={i}
              className={`pgn-page-btn${p === currentPage ? " pgn-page-btn--active" : ""}`}
              onClick={() => handlePageChange(p as number)}
            >
              {p}
            </button>
          )
        )}

        {/* Next */}
        <button
          className={`pgn-nav-btn${isLast ? " pgn-nav-btn--disabled" : ""}`}
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={isLast}
          aria-label="Next page"
        >
          <MdChevronRight size={18} />
        </button>
      </div>

      {/* Right: page size selector */}
      <div className="pgn-size">
        <span className="pgn-size-label">Rows per page</span>
        <select
          value={pageSize}
          onChange={(e) => { handlePageChange(1); setPageSize(Number(e.target.value)); }}
          className="pgn-size-select"
        >
          {arraySize?.map((p: number) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .pgn-root {
          font-family: 'Plus Jakarta Sans', sans-serif;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          padding: 14px 20px;
        }

        /* Info */
        .pgn-info {
          display: flex; align-items: center; gap: 5px;
          font-size: 13px; color: #64748b; font-weight: 500;
        }
        .pgn-info-current {
          font-size: 13px; font-weight: 700; color: #1d4ed8;
          background: #eff6ff; border: 1.5px solid #bfdbfe;
          border-radius: 6px; padding: 2px 8px;
        }
        .pgn-info-total { font-size: 13px; font-weight: 600; color: #374151; }

        /* Pages */
        .pgn-pages {
          display: flex; align-items: center; gap: 4px; flex-wrap: wrap;
        }

        .pgn-nav-btn {
          width: 34px; height: 34px; border-radius: 9px;
          display: flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg, #1d4ed8, #2563eb);
          border: none; color: #fff; cursor: pointer;
          transition: transform 0.15s, box-shadow 0.18s;
          box-shadow: 0 2px 8px rgba(37,99,235,0.28);
          flex-shrink: 0;
        }
        .pgn-nav-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(37,99,235,0.38); }
        .pgn-nav-btn:active { transform: translateY(0); }
        .pgn-nav-btn--disabled {
          background: #e2e8f0 !important; color: #94a3b8 !important;
          cursor: not-allowed !important; box-shadow: none !important; transform: none !important;
        }

        .pgn-page-btn {
          min-width: 34px; height: 34px; padding: 0 6px;
          border-radius: 9px; border: 1.5px solid #e2e8f0;
          background: #ffffff; color: #475569;
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px; font-weight: 600; cursor: pointer;
          transition: all 0.15s;
        }
        .pgn-page-btn:hover { border-color: #93c5fd; color: #2563eb; background: #eff6ff; }
        .pgn-page-btn--active {
          background: linear-gradient(135deg, #1d4ed8, #2563eb) !important;
          border-color: transparent !important; color: #fff !important;
          box-shadow: 0 2px 8px rgba(37,99,235,0.28);
        }

        .pgn-ellipsis {
          font-size: 14px; color: #94a3b8; font-weight: 600;
          padding: 0 4px; line-height: 34px;
          user-select: none;
        }

        /* Size */
        .pgn-size {
          display: flex; align-items: center; gap: 8px;
        }
        .pgn-size-label { font-size: 12.5px; font-weight: 600; color: #64748b; white-space: nowrap; }
        .pgn-size-select {
          height: 34px; padding: 0 10px;
          background: #f8fafc; border: 1.5px solid #e2e8f0;
          border-radius: 9px; font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 13px; font-weight: 600; color: #374151;
          cursor: pointer; outline: none;
          transition: border-color 0.15s;
        }
        .pgn-size-select:hover { border-color: #93c5fd; }
        .pgn-size-select:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,0.1); }

        @media (max-width: 600px) {
          .pgn-root { justify-content: center; }
          .pgn-info { display: none; }
        }
      `}</style>
    </div>
  );
};

export default Pagination;